"use client";

import { useEffect, useState, useMemo } from "react";
import { createLogger } from "@/lib/logger";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ChartErrorBoundary } from "@/app/components/ChartErrorBoundary";
import { useT } from "@/hooks/useT";

const logger = createLogger("TraderPnLChart");

interface HistoryData {
  period: string;
  data: Array<{
    timestamp: number;
    pnl: number;
    type: string;
  }>;
  startPnL: number;
  currentPnL: number;
  change: number;
  changePercent: number;
}

function TraderPnLChartContent({ wallet, source = 'polymarket', initialPnlTimeseries }: {
  wallet: string;
  source?: string;
  initialPnlTimeseries?: Array<{ t: number; v: number }>;
}) {
  const t = useT();
  const isPredict = source === 'predict';
  const isKalshi = source === 'kalshi';
  // Period configs are translated at render-time so the displayLabel
  // ("Past Day", "All-Time", …) flips with the user's locale.
  const PERIODS = useMemo(() => {
    if (isKalshi) return [
      { value: "week", label: "1W", displayLabel: t("traders.chart.display.week") },
      { value: "month", label: "1M", displayLabel: t("traders.chart.display.month") },
      { value: "year", label: "1Y", displayLabel: t("traders.chart.display.year") },
      { value: "all", label: "ALL", displayLabel: t("traders.chart.display.all") },
    ] as const;
    if (isPredict) return [
      { value: "_1D", label: "1D", displayLabel: t("traders.chart.display.day") },
      { value: "_7D", label: "7D", displayLabel: t("traders.chart.display.week") },
      { value: "_30D", label: "30D", displayLabel: t("traders.chart.display.month") },
      { value: "MAX", label: "ALL", displayLabel: t("traders.chart.display.all") },
    ] as const;
    return [
      { value: "1d", label: "1D", displayLabel: t("traders.chart.display.day") },
      { value: "7d", label: "7D", displayLabel: t("traders.chart.display.week") },
      { value: "30d", label: "30D", displayLabel: t("traders.chart.display.month") },
      { value: "all", label: "ALL", displayLabel: t("traders.chart.display.all") },
    ] as const;
  }, [isKalshi, isPredict, t]);
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>(isKalshi ? "month" : isPredict ? "MAX" : "all");

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);

        // Predict.fun: fetch pnlTimeseries from GraphQL (public, needs checksum addr)
        if (isPredict) {
          // Use cached data for MAX if available
          if (period === 'MAX' && initialPnlTimeseries && initialPnlTimeseries.length > 0) {
            const data = initialPnlTimeseries;
            setHistory({
              period,
              data: data.map(p => ({ timestamp: p.t, pnl: p.v, type: 'predict' })),
              startPnL: data[0]?.v || 0,
              currentPnL: data[data.length - 1]?.v || 0,
              change: (data[data.length - 1]?.v || 0) - (data[0]?.v || 0),
              changePercent: data[0]?.v ? (((data[data.length - 1]?.v || 0) - data[0].v) / Math.abs(data[0].v)) * 100 : 0,
            });
            setError(null);
            return;
          }

          // Fetch via our backend proxy (avoids CORS with graphql.predict.fun)
          const proxyRes = await fetch(`/api/predict/pnl-timeseries?address=${encodeURIComponent(wallet)}&interval=${period}`);
          if (proxyRes.ok) {
            const proxyData = await proxyRes.json();
            const tsData = proxyData.data || [];
            if (tsData.length > 0) {
              const points = tsData.map((p: any) => ({ timestamp: p.t, pnl: p.v, type: 'predict' }));
              setHistory({
                period,
                data: points,
                startPnL: points[0].pnl,
                currentPnL: points[points.length - 1].pnl,
                change: points[points.length - 1].pnl - points[0].pnl,
                changePercent: points[0].pnl ? ((points[points.length - 1].pnl - points[0].pnl) / Math.abs(points[0].pnl)) * 100 : 0,
              });
              setError(null);
              return;
            }
          }

          setError(t("traders.chart.noData"));
          return;
        }

        // Kalshi/DFlow: fetch from DFlow analytics endpoint.
        // Skip when the trader has no Solana wallet mapped (wallet falls back to the Kalshi nickname)
        // — otherwise the DFlow endpoint 400s on an invalid base58 address.
        if (isKalshi) {
          const isSolanaAddress = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(wallet);
          if (!isSolanaAddress) {
            setError(t("traders.chart.noData"));
            return;
          }
          const dflowRes = await fetch(`/api/analytics/dflow/wallet/${encodeURIComponent(wallet)}/pnl?granularity=${period}`);
          if (dflowRes.ok) {
            const dflowData = await dflowRes.json();
            if (dflowData?.success && dflowData.data?.timeSeries?.length > 0) {
              const ts = dflowData.data.timeSeries;
              const points = ts.map((p: any) => ({
                timestamp: p.timestamp || p.t,
                pnl: p.pnl ?? p.realized_pnl ?? 0,
                type: 'dflow',
              }));
              const startPnL = points[0].pnl;
              const currentPnL = points[points.length - 1].pnl;
              setHistory({
                period,
                data: points,
                startPnL,
                currentPnL,
                change: currentPnL - startPnL,
                changePercent: startPnL ? ((currentPnL - startPnL) / Math.abs(startPnL)) * 100 : 0,
              });
              setError(null);
              return;
            }
          }
          setError(t("traders.chart.noData"));
          return;
        }

        // Polymarket: Try Predexon PnL first (richer data with realized + unrealized breakdown)
        const predexonRes = await fetch(`/api/analytics/wallet/${wallet}/pnl`);
        if (predexonRes.ok) {
          const predexonData = await predexonRes.json();
          if (predexonData?.success && predexonData.data?.timeSeries?.length > 0) {
            const ts = predexonData.data.timeSeries;
            setHistory({
              period,
              data: ts.map((p: any) => ({ timestamp: p.timestamp || p.t, pnl: p.realized_pnl ?? p.pnl ?? 0, type: 'predexon' })),
              startPnL: ts[0]?.realized_pnl ?? ts[0]?.pnl ?? 0,
              currentPnL: predexonData.data.realized_pnl ?? ts[ts.length - 1]?.pnl ?? 0,
              change: (predexonData.data.realized_pnl ?? 0) - (ts[0]?.realized_pnl ?? ts[0]?.pnl ?? 0),
              changePercent: predexonData.data.roi ? predexonData.data.roi * 100 : 0,
            });
            setError(null);
            return;
          }
        }

        // Fallback to original endpoint
        const response = await fetch(`/api/traders/${wallet}/pnl-history?period=${period}`);
        if (!response.ok) throw new Error("Failed to fetch P&L history");
        const data = await response.json();
        setHistory(data);
        setError(null);
      } catch (err) {
        logger.error("Error fetching P&L history", { error: err instanceof Error ? err.message : String(err) });
        setError(t("traders.chart.loadFail"));
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [wallet, period, isPredict, isKalshi]);

  const formatCurrency = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
      return `${value >= 0 ? '' : '-'}$${(absValue / 1000000).toFixed(2)}M`;
    } else if (absValue >= 1000) {
      return `${value >= 0 ? '' : '-'}$${(absValue / 1000).toFixed(2)}K`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    if (period === "1d") {
      return date.toLocaleTimeString("en-US", { hour: "numeric", hour12: true });
    }
    if (period === "7d") {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Use change for the period, not total PnL
  const periodChange = history ? history.change : 0;
  const isPositive = periodChange >= 0;
  const chartColor = isPositive ? "#00FF9D" : "#FF1744";
  const currentPeriod = PERIODS.find(p => p.value === period);

  // 2026-05-22 (Pika): when the chart refetches after a period
  // toggle, the previous code returned the whole-component skeleton
  // and the header (title + value + period tabs) blinked out. The
  // user lost the timeframe context mid-click. Now: full skeleton
  // only on the very first load (when `history` is still null);
  // subsequent refetches keep the header + tabs visible and
  // skeleton ONLY the chart area, so the active period stays
  // highlighted while new data lands.
  if (loading && !history) {
    return (
      <section>
        <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors duration-300 p-6">
          {/* Skeleton matches loaded layout (title + value row + period tabs + chart) to prevent CLS */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-pulse">
            <div className="space-y-2">
              <div className="h-6 w-28 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-9 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-9 w-56 bg-gray-200 dark:bg-gray-700 rounded-[5px]" />
          </div>
          <div className="h-[200px] bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </section>
    );
  }

  if (error || !history || history.data.length === 0) {
    return (
      <section>
        <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors duration-300 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-monument text-xl font-bold text-gray-900 dark:text-white">
              {t("traders.chart.title")}
            </h2>
            <div className="flex gap-1 bg-gray-100 dark:bg-[#1A1A1A] rounded-[5px] p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 rounded-[5px] text-xs font-bold transition-colors ${
                    period === p.value
                      ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <p className="font-satoshi text-gray-500 dark:text-gray-400 text-center py-12">
            {t("traders.chart.noTradingHistory")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors duration-300 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-monument text-xl font-bold text-gray-900 dark:text-white mb-1">
              Profit/Loss
            </h2>
            <div className="flex items-center gap-3">
              <span className={`font-monument text-3xl font-bold ${isPositive ? "text-[#00FF9D]" : "text-[#FF1744]"}`}>
                {isPositive ? "+" : ""}{formatCurrency(periodChange)}
              </span>
              <span className="font-satoshi text-sm text-gray-500 dark:text-gray-400">
                {currentPeriod?.displayLabel || t("traders.chart.display.all")}
              </span>
            </div>
          </div>

          {/* Period Selector — 2026-05-22 (Pika): active state was
              `dark:bg-[#18181D]` against an outer `dark:bg-[#1A1A1A]`
              container, basically the same colour, so the user
              couldn't tell which period was active. Switched to the
              canonical gray-900/white active style used by every
              other tab/pill in the product (/activity, /copytrading,
              /portfolio). */}
          <div className="flex gap-1 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-[5px] text-xs font-bold transition-colors ${
                  period === p.value
                    ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart — wrapper holds the loading overlay so the header
            (Profit/Loss heading + period buttons) above stays
            visible during a refetch. Without the overlay the whole
            section would jump to skeleton and the user would lose
            the active period highlight mid-click. */}
        <div className="relative">
          {loading && history && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 dark:bg-[#18181D]/40 rounded backdrop-blur-[1px]">
              <div className="h-6 w-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        <ResponsiveContainer width="100%" height={200} minHeight={1}>
          <AreaChart data={history.data.map(d => ({ ...d, date: formatDate(d.timestamp) }))}>
            <defs>
              <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 11 }}
              dy={10}
            />
            <YAxis
              hide
              domain={['dataMin', 'dataMax']}
            />
            {/* 2026-05-22 (Pika): horizontal reference line at $0 so
                the user can read "this period was profitable" at a
                glance — the area chart's baseline alone doesn't make
                that obvious when domain is ['dataMin', 'dataMax']
                and the whole shape sits above (or below) zero. */}
            <ReferenceLine
              y={0}
              stroke="rgba(156, 163, 175, 0.35)"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              labelStyle={{ color: '#9CA3AF', fontSize: 12, marginBottom: 4 }}
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [
                  <span key="value" className={`font-bold ${numValue >= 0 ? 'text-[#00FF9D]' : 'text-[#FF1744]'}`}>
                    {numValue >= 0 ? '+' : ''}{formatCurrency(numValue)}
                  </span>,
                  t("traders.chart.tooltip.pnl")
                ];
              }}
            />
            <Area
              type="monotone"
              dataKey="pnl"
              stroke={chartColor}
              strokeWidth={2}
              fill="url(#pnlGradient)"
              dot={false}
              activeDot={{
                r: 6,
                fill: chartColor,
                stroke: '#fff',
                strokeWidth: 2
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

// Wrap with error boundary for production resilience
export default function TraderPnLChart({ wallet, source, pnlTimeseries }: {
  wallet: string;
  source?: string;
  pnlTimeseries?: Array<{ t: number; v: number }>;
}) {
  const t = useT();
  return (
    <ChartErrorBoundary
      chartName="Trader P&L Chart"
      height="h-[300px]"
      errorLabel={t("chartError.failedToLoad", { chart: t("chartError.traderPnl") })}
      retryLabel={t("chartError.retry")}
    >
      <TraderPnLChartContent wallet={wallet} source={source} initialPnlTimeseries={pnlTimeseries} />
    </ChartErrorBoundary>
  );
}
